/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { ApolloError } from "apollo-client";
import { useUser } from "@shortlyster/auth-layer";
import { FetchProfile } from "./queries";
import { useAuth } from "react-oidc-context";
import { useApolloClients } from "./AuthorizedApolloProvider";
import { Alert } from "@compono/ui";

/** apollo-link-http reports a non-2xx response as a ServerError carrying the status. */
type ServerError = { statusCode?: number };

const statusOf = (error?: ApolloError) =>
  (error?.networkError as ServerError | null)?.statusCode;

/**
 * An unusable token. `createErrorLink` already redirects to sign-in on a 401, so
 * this component only has to stay out of its way — two redirects racing for
 * `window.location` would decide by whichever landed last, and signing the user
 * out is the wrong outcome when re-authenticating is what they need.
 */
const isUnauthenticated = (error?: ApolloError) => statusOf(error) === 401;

/**
 * A signed-in user the platform refuses, which is this component's to handle.
 *
 * Reading the status alone is not enough: the subgraphs' graphql-shield fallback
 * answers HTTP 200 carrying `code: FORBIDDEN`, because yoga strips the `http`
 * extension that would set a status but leaves `code` intact.
 */
const isForbidden = (error?: ApolloError) =>
  statusOf(error) === 403 ||
  !!error?.graphQLErrors.some(
    (graphQLError) => graphQLError.extensions?.code === "FORBIDDEN",
  );

/**
 * The subgraph's graphql-shield gate on `User.accesses` denies that one field in
 * place: 200, the profile intact, `accesses` null and a FORBIDDEN error against
 * its path. That is a definite "not an admin" — unlike a null `accesses` with
 * nothing said about it, which means the field was allowed and came back empty.
 */
const isAccessesDenied = (error?: ApolloError) =>
  !!error?.graphQLErrors.some(
    (graphQLError) =>
      graphQLError.extensions?.code === "FORBIDDEN" &&
      graphQLError.path?.[graphQLError.path.length - 1] === "accesses",
  );

/**
 * Every denial used to read "You do not have permission", and the alert then
 * clears the session and redirects, taking the network log with it — so this one
 * sentence is all anyone gets to work from. Say which denial happened.
 *
 * `accesses` coming back null is worth its own message rather than being read as
 * a plain denial: the field is nullable and the subgraph can drop it while still
 * answering 200 with a profile, at which point the account looks exactly like one
 * that holds no apps. Blaming the account there sends the reader the wrong way.
 */
const REFUSED_BY_PLATFORM = "You do not have permission";
const MISSING_ADMIN_ACCESS = "Your account does not have Compono Admin access";
const UNREADABLE_ACCESS =
  "Your permissions could not be read, so Compono Admin cannot let you in. This is a fault to report, not a problem with your account.";

/** Apollo messages usually end in a full stop; the sentence around them supplies its own. */
const withoutTrailingStop = (message: string) => message.replace(/\.\s*$/, "");

export const AuthFetcher: React.FC = ({ children }) => {
  const { signoutRedirect } = useAuth();
  const [authUser, setUser] = useUser();
  const { federationGatewayClient } = useApolloClients()!;
  const [ready, setReady] = useState(false);

  // `errorPolicy: "all"` because a denial arrives as a partial response: the
  // profile alongside a field-level FORBIDDEN. Apollo's default drops `data`
  // whenever any error is present, which threw away the half that says which
  // denial this is.
  const { data, loading, error } = useQuery(FetchProfile, {
    client: federationGatewayClient,
    errorPolicy: "all",
  });

  const profile = data?.myProfile;
  const accesses = profile?.accesses;

  const isAdmin = !!accesses?.genericApps?.some(
    (app) => app.appCode === "compono-admin",
  );

  const signout = (errorMsg: string) => {
    window.alert(errorMsg);
    setUser(null);
    window.sessionStorage.clear();
    signoutRedirect({
      post_logout_redirect_uri: window.location.origin,
    });
  };

  // A profile that loads but carries no `compono-admin` app is not an error, so
  // it has to be denied on its own. Reading it as one left `errorMsg` at
  // 'Unknown error', which no denial check matches, so a non-admin fell through
  // every branch and got the whole admin shell with each query failing behind it.
  useEffect(() => {
    if (loading) return;

    if (isUnauthenticated(error)) return;

    if (!profile) {
      if (isForbidden(error)) signout(REFUSED_BY_PLATFORM);
      return;
    }

    if (isAdmin) return;

    const accessWasAnswered = !!accesses || isAccessesDenied(error);

    signout(accessWasAnswered ? MISSING_ADMIN_ACCESS : UNREADABLE_ACCESS);
  }, [loading, error, profile, accesses, isAdmin]);

  useEffect(() => {
    if (loading) return;
    if (data?.myProfile && !error) {
      setUser(data.myProfile).then(() => setReady(true));
    } else if (authUser) {
      setUser(null).then(() => setReady(true));
    } else {
      setReady(true);
    }
  }, [loading, data]);

  // Anything other than an auth failure used to render nothing at all, which
  // left the app a blank page when the API was down or the profile query broke.
  // A 401 is excluded too: the error link is mid-redirect to sign-in, and an
  // alert telling the user to reload would only flash over it.
  if (error && !isForbidden(error) && !isUnauthenticated(error)) {
    return (
      <Alert tone="critical" title="Could not load your admin profile">
        {withoutTrailingStop(error.message)}. Reload the page to try again.
      </Alert>
    );
  }

  return ready && !error ? <>{children}</> : null;
};
